import React, { useEffect, useMemo, useState, Children, Component } from 'react';
import { School, Users, GraduationCap, Heart, BookOpen, ShieldCheck, Activity, TrendingUp, AlertTriangle, CheckCircle2, Clock, Zap, Server, RefreshCw, Layers, ImageIcon, UserPlus, Database, FileText, PlayCircle, ChevronDown } from 'lucide-react';
import { motion } from 'framer-motion';
import { PageLayout } from '../shared/PageLayout';
import { StatCard } from '../shared/StatCard';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { useAppStore } from '../../../../hooks/useAppStore';
import { isStrictMode } from '../../../../utils/strictMode';
import { runPipelineBatch } from '../../../../governance/pipeline/orchestrator';
import type { PipelineBatchResult } from '../../../../governance/pipeline/issueContract';
import { ALL_QUESTIONS_CORE, QUESTIONS_BY_FAMILY } from '../../../../data/questionLoader';
import { Question } from '../../../../types';
import { computeLiveSystemStats } from '../../../../governance/systemStats';
import { TAXONOMY } from '../../../../config/taxonomyRegistry';
import { VISUAL_REGISTRY } from '../../../../visuals/registry/visualRegistry';
const SAMPLE_SIZE_OPTIONS = [50, 100, 250, 0] as const; // 0 = all
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 24
    }
  }
};
const RingChart = ({
  score,
  label,
  subtitle,
  size = 'md'
}: {
  score: number;
  label: string;
  subtitle: string;
  size?: 'sm' | 'md';
}) => {
  const radius = size === 'sm' ? 28 : 36;
  const svgSize = size === 'sm' ? 72 : 96;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - score / 100 * circumference;
  const color = score >= 80 ? 'text-emerald-500' : score >= 60 ? 'text-amber-500' : 'text-red-500';
  return <div className="bg-white rounded-xl border border-gray-200 shadow-md hover:shadow-lg transition-shadow duration-200 p-5 flex items-center gap-6" data-id="element-458">
      <div className="relative flex items-center justify-center flex-shrink-0" data-id="element-459">
        <svg className="transform -rotate-90" style={{
        width: svgSize,
        height: svgSize
      }} data-id="element-460">
          <circle cx={svgSize / 2} cy={svgSize / 2} r={radius} stroke="currentColor" strokeWidth="8" fill="transparent" className="text-gray-100" data-id="element-461" />
          <motion.circle cx={svgSize / 2} cy={svgSize / 2} r={radius} stroke="currentColor" strokeWidth="8" fill="transparent" strokeDasharray={circumference} initial={{
          strokeDashoffset: circumference
        }} animate={{
          strokeDashoffset
        }} transition={{
          duration: 1.5,
          ease: 'easeOut'
        }} className={color} strokeLinecap="round" data-id="element-462" />
        </svg>
        <div className="absolute flex flex-col items-center justify-center" data-id="element-463">
          <span className={`font-bold text-gray-900 ${size === 'sm' ? 'text-lg' : 'text-xl'}`} data-id="element-464">
            {score}%
          </span>
        </div>
      </div>
      <div data-id="element-465">
        <h3 className="text-sm font-bold text-gray-900 mb-1" data-id="element-466">{label}</h3>
        <p className="text-xs text-gray-500 mb-2" data-id="element-467">{subtitle}</p>
        <div className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${score >= 80 ? 'bg-emerald-50 text-emerald-700' : score >= 60 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`} data-id="element-468">
          <ShieldCheck className="w-3.5 h-3.5" data-id="element-469" />
          {score >= 80 ? 'Gezond' : score >= 60 ? 'Aandacht nodig' : 'Kritiek'}
        </div>
      </div>
    </div>;
};
interface QuickActionProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  variant?: 'default' | 'primary';
}
function QuickAction({
  icon,
  label,
  onClick,
  variant = 'default'
}: QuickActionProps) {
  return <motion.button whileHover={{
    scale: 1.02
  }} whileTap={{
    scale: 0.98
  }} onClick={onClick} className={`flex flex-col items-center justify-center gap-2 p-4 rounded-xl border transition-all ${variant === 'primary' ? 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 hover:border-blue-300' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300'}`} data-id="element-470">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${variant === 'primary' ? 'bg-blue-100' : 'bg-gray-100'}`} data-id="element-471">
        {icon}
      </div>
      <span className="text-xs font-semibold text-center" data-id="element-472">{label}</span>
    </motion.button>;
}
export function AdminDashboardHome({
  onNavigate
}: {
  onNavigate?: (view: string) => void;
}) {
  const {
    accounts,
    getLearnerData
  } = useAppStore();
  const [pipelineResult, setPipelineResult] = useState<PipelineBatchResult | null>(null);
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [strictModeOn] = useState(() => isStrictMode());
  const [sampleSize, setSampleSize] = useState<number>(100);
  const [sampleDropdownOpen, setSampleDropdownOpen] = useState(false);
  const actualSampleSize = sampleSize === 0 ? ALL_QUESTIONS_CORE.length : sampleSize;
  useEffect(() => {
    setPipelineLoading(true);
    const timer = setTimeout(() => {
      try {
        const sample = ALL_QUESTIONS_CORE.slice(0, actualSampleSize) as unknown as Question[];
        const result = runPipelineBatch(sample);
        setPipelineResult(result);
      } catch {
        // silent
      } finally {
        setPipelineLoading(false);
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [actualSampleSize]);
  const handleRefreshPipeline = () => {
    setPipelineLoading(true);
    setPipelineResult(null);
    setTimeout(() => {
      try {
        const sample = ALL_QUESTIONS_CORE.slice(0, actualSampleSize) as unknown as Question[];
        const result = runPipelineBatch(sample);
        setPipelineResult(result);
      } catch {
        // silent
      } finally {
        setPipelineLoading(false);
      }
    }, 50);
  };
  const stats = useMemo(() => {
    const systemStats = computeLiveSystemStats();
    const allAccounts = accounts?.accountsById ? Object.values(accounts.accountsById) : [];
    const learners = allAccounts.filter(a => a.role === 'learner');
    const teachers = allAccounts.filter(a => a.role === 'teacher');
    const parents = allAccounts.filter(a => a.role === 'parent');
    const schools = new Set(allAccounts.map(a => (a as any).schoolName).filter(Boolean));
    const now = Date.now();
    const todayThreshold = now - 24 * 60 * 60 * 1000;
    const weekThreshold = now - 7 * 24 * 60 * 60 * 1000;
    const activeToday = learners.filter(l => l.lastActive && l.lastActive > todayThreshold).length;
    const activeThisWeek = learners.filter(l => l.lastActive && l.lastActive > weekThreshold).length;
    const freeLearners = learners.filter(l => !(l as any).isPlusUser).length;
    const plusLearners = learners.filter(l => (l as any).isPlusUser).length;
    const total = learners.length || 1;
    const planDistribution = [{
      label: 'Gratis',
      count: freeLearners,
      pct: Math.round(freeLearners / total * 100),
      color: 'bg-gray-400'
    }, {
      label: 'Plus',
      count: plusLearners,
      pct: Math.round(plusLearners / total * 100),
      color: 'bg-blue-500'
    }];
    const topLearners = learners.map(l => {
      const data = getLearnerData(l.id);
      return {
        name: l.displayName,
        xp: data?.stats?.xp ?? 0
      };
    }).sort((a, b) => b.xp - a.xp).slice(0, 3);
    return {
      totalLearners: learners.length,
      totalTeachers: teachers.length,
      totalParents: parents.length,
      totalSchools: schools.size,
      totalQuestions: systemStats.totalQuestions || ALL_QUESTIONS_CORE.length,
      activeToday,
      activeThisWeek,
      planDistribution,
      topLearners
    };
  }, [accounts, getLearnerData]);
  const passCount = pipelineResult?.verdicts.PASS ?? null;
  const warnCount = pipelineResult?.verdicts.REVIEW_NEEDED ?? null;
  const failCount = pipelineResult?.verdicts.FAIL ?? null;
  const avgScore = pipelineResult?.avgScore ?? null;
  const totalValidated = passCount !== null ? passCount + (warnCount ?? 0) + (failCount ?? 0) : 0;
  const systemHealth = useMemo(() => {
    let storageUsed = 0;
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) {
          storageUsed += localStorage.getItem(key)?.length || 0;
        }
      }
    } catch {
      // Ignore
    }
    const storageMb = (storageUsed / 1024 / 1024).toFixed(2);
    const storagePct = Math.min(100, Math.round(parseFloat(storageMb) / 5 * 100));
    let responseTime = 45;
    try {
      if (window.performance && window.performance.timing) {
        responseTime = window.performance.timing.responseEnd - window.performance.timing.requestStart;
      }
    } catch {
      // Ignore
    }
    return {
      storageMb,
      storagePct,
      responseTime: Math.max(10, responseTime)
    };
  }, []);
  const contentStats = useMemo(() => {
    const totalFamilies = QUESTIONS_BY_FAMILY.size;
    const taxonomyFamilies = TAXONOMY.length;
    const coveragePct = taxonomyFamilies > 0 ? Math.round(totalFamilies / taxonomyFamilies * 100) : 0;
    const typeDistribution: Record<string, number> = {};
    let totalQuestions = 0;
    ALL_QUESTIONS_CORE.forEach(q => {
      typeDistribution[q.questionType] = (typeDistribution[q.questionType] || 0) + 1;
      totalQuestions++;
    });
    const registeredVisuals = Object.keys(VISUAL_REGISTRY).length;
    const usedVisuals = new Set<string>();
    ALL_QUESTIONS_CORE.forEach(q => {
      const aids = [...(q.stimulusAids || []), ...(q.supportAids || [])];
      aids.forEach(aid => {
        if (aid.type) usedVisuals.add(aid.type);
      });
    });
    return {
      totalFamilies,
      taxonomyFamilies,
      coveragePct,
      typeDistribution,
      totalQuestions,
      registeredVisuals,
      usedVisualsCount: usedVisuals.size,
      visualCoveragePct: registeredVisuals > 0 ? Math.round(usedVisuals.size / registeredVisuals * 100) : 0
    };
  }, []);
  const RECENT_ACTIVITY = useMemo(() => {
    const activities = [];
    const now = new Date();
    const formatTime = (offset: number) => new Date(now.getTime() - offset).toLocaleTimeString('nl-NL', {
      hour: '2-digit',
      minute: '2-digit'
    });
    if (stats.totalLearners > 0) {
      activities.push({
        time: formatTime(0),
        message: `${stats.totalLearners} leerlingen actief in het systeem`,
        type: 'info' as const
      });
    }
    if (pipelineResult) {
      activities.push({
        time: formatTime(60000),
        message: `Validatie: ${passCount} geslaagd, ${failCount} gefaald`,
        type: (failCount ?? 0) > 0 ? 'warning' as const : 'success' as const
      });
    }
    if (stats.totalSchools > 0) {
      activities.push({
        time: formatTime(3600000),
        message: `${stats.totalSchools} scholen aangesloten op het platform`,
        type: 'info' as const
      });
    }
    activities.push({
      time: formatTime(7200000),
      message: `Vragenbank: ${stats.totalQuestions} vragen geladen`,
      type: 'success' as const
    });
    return activities;
  }, [stats, pipelineResult, passCount, failCount]);
  // Quick action handlers (placeholders)
  const handleAddUser = () => {
    if (onNavigate) onNavigate('user-management');
  };
  const handleOpenQuestionBank = () => {
    if (onNavigate) onNavigate('question-bank');
  };
  const handleOpenLogs = () => {
    if (onNavigate) onNavigate('logs');
  };
  return <PageLayout title="Dashboard" subtitle="Overzicht van het Meester djay platform" actions={<ConnectionBadge status="live" data-id="element-474" />} data-id="element-473">
      {/* Top Stats Row */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-id="element-475">
        <motion.div variants={itemVariants} data-id="element-476">
          <StatCard label="Scholen" value={stats.totalSchools} icon={<School className="w-5 h-5" data-id="element-478" />} color="purple" data-id="element-477" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-479">
          <StatCard label="Docenten" value={stats.totalTeachers} icon={<GraduationCap className="w-5 h-5" data-id="element-481" />} color="blue" data-id="element-480" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-482">
          <StatCard label="Leerlingen" value={stats.totalLearners} icon={<Users className="w-5 h-5" data-id="element-484" />} color="emerald" data-id="element-483" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-485">
          <StatCard label="Ouders" value={stats.totalParents} icon={<Heart className="w-5 h-5" data-id="element-487" />} color="rose" data-id="element-486" />
        </motion.div>
      </motion.div>

      {/* Quick Actions Row */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="mb-6" data-id="element-488">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3" data-id="element-489">
          Snelle Acties
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-490">
          <motion.div variants={itemVariants} data-id="element-491">
            <QuickAction icon={<PlayCircle className="w-5 h-5" data-id="element-493" />} label="Validatie uitvoeren" onClick={handleRefreshPipeline} variant="primary" data-id="element-492" />
          </motion.div>
          <motion.div variants={itemVariants} data-id="element-494">
            <QuickAction icon={<UserPlus className="w-5 h-5" data-id="element-496" />} label="Gebruiker toevoegen" onClick={handleAddUser} data-id="element-495" />
          </motion.div>
          <motion.div variants={itemVariants} data-id="element-497">
            <QuickAction icon={<Database className="w-5 h-5" data-id="element-499" />} label="Vragenbank" onClick={handleOpenQuestionBank} data-id="element-498" />
          </motion.div>
          <motion.div variants={itemVariants} data-id="element-500">
            <QuickAction icon={<FileText className="w-5 h-5" data-id="element-502" />} label="Activiteitenlog" onClick={handleOpenLogs} data-id="element-501" />
          </motion.div>
        </div>
      </motion.div>

      {/* Content Stats Row */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-id="element-503">
        <motion.div variants={itemVariants} data-id="element-504">
          <StatCard label="Vragenbank" value={stats.totalQuestions} subtitle="vragen totaal" icon={<BookOpen className="w-5 h-5" data-id="element-506" />} color="blue" data-id="element-505" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-507">
          {avgScore !== null ? <RingChart score={avgScore} label="Kwaliteitsscore" subtitle={`steekproef ${actualSampleSize} vragen`} size="sm" data-id="element-508" /> : <StatCard label="Kwaliteitsscore" value="laden..." subtitle="steekproef wordt geanalyseerd" icon={<ShieldCheck className="w-5 h-5" data-id="element-510" />} color="amber" data-id="element-509" />}
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-511">
          <RingChart score={contentStats.coveragePct} label="Seed Dekking" subtitle={`${contentStats.totalFamilies} / ${contentStats.taxonomyFamilies} families`} size="sm" data-id="element-512" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-513">
          <StatCard label="Actief Vandaag" value={stats.activeToday} subtitle="leerlingen" icon={<Activity className="w-5 h-5" data-id="element-515" />} color="purple" data-id="element-514" />
        </motion.div>
      </motion.div>

      {/* Content Coverage Cards */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6" data-id="element-516">
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-md hover:shadow-lg transition-shadow duration-200 p-5" data-id="element-517">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-518">
            <BookOpen className="w-4 h-4" data-id="element-519" />
            Content Dekking
          </h3>
          <div className="flex items-end justify-between mb-2" data-id="element-520">
            <span className="text-2xl font-bold text-gray-900" data-id="element-521">
              {contentStats.coveragePct}%
            </span>
            <span className="text-sm text-gray-500 mb-1" data-id="element-522">
              {contentStats.totalFamilies} / {contentStats.taxonomyFamilies}{' '}
              families
            </span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-523">
            <motion.div initial={{
            width: 0
          }} animate={{
            width: `${contentStats.coveragePct}%`
          }} transition={{
            duration: 1,
            delay: 0.2
          }} className={`h-full rounded-full ${contentStats.coveragePct >= 80 ? 'bg-emerald-500' : contentStats.coveragePct >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} data-id="element-524" />
          </div>
        </motion.div>

        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-md hover:shadow-lg transition-shadow duration-200 p-5" data-id="element-525">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-526">
            <Layers className="w-4 h-4" data-id="element-527" />
            Vraagtypen
          </h3>
          <div className="flex items-end justify-between mb-2" data-id="element-528">
            <span className="text-2xl font-bold text-gray-900" data-id="element-529">
              {Object.keys(contentStats.typeDistribution).length}
            </span>
            <span className="text-sm text-gray-500 mb-1" data-id="element-530">actieve typen</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex" data-id="element-531">
            {Object.entries(contentStats.typeDistribution).sort((a, b) => b[1] - a[1]).map(([type, count], i) => {
            const colors = ['bg-blue-500', 'bg-indigo-500', 'bg-purple-500', 'bg-pink-500', 'bg-rose-500', 'bg-orange-500', 'bg-amber-500', 'bg-emerald-500', 'bg-teal-500', 'bg-cyan-500'];
            return <div key={type} className={`h-full ${colors[i % colors.length]}`} style={{
              width: `${count / contentStats.totalQuestions * 100}%`
            }} title={`${type}: ${count}`} data-id="element-532" />;
          })}
          </div>
        </motion.div>

        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-md hover:shadow-lg transition-shadow duration-200 p-5" data-id="element-533">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-534">
            <ImageIcon className="w-4 h-4" data-id="element-535" />
            Visuele Componenten
          </h3>
          <div className="flex items-end justify-between mb-2" data-id="element-536">
            <span className="text-2xl font-bold text-gray-900" data-id="element-537">
              {contentStats.visualCoveragePct}%
            </span>
            <span className="text-sm text-gray-500 mb-1" data-id="element-538">
              {contentStats.usedVisualsCount} / {contentStats.registeredVisuals}{' '}
              in gebruik
            </span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-539">
            <motion.div initial={{
            width: 0
          }} animate={{
            width: `${contentStats.visualCoveragePct}%`
          }} transition={{
            duration: 1,
            delay: 0.4
          }} className={`h-full rounded-full ${contentStats.visualCoveragePct >= 80 ? 'bg-emerald-500' : contentStats.visualCoveragePct >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} data-id="element-540" />
          </div>
        </motion.div>
      </motion.div>

      {/* Activity & System Health Row */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" data-id="element-541">
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-md hover:shadow-lg transition-shadow duration-200 p-6" data-id="element-542">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2" data-id="element-543">
            <Clock className="w-4 h-4 text-gray-400" data-id="element-544" />
            Recente Activiteit
          </h3>
          <div className="space-y-3" data-id="element-545">
            {RECENT_ACTIVITY.map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-546">
                <div className={`mt-0.5 flex-shrink-0 ${item.type === 'success' ? 'text-emerald-500' : item.type === 'warning' ? 'text-amber-500' : 'text-blue-500'}`} data-id="element-547">
                  {item.type === 'success' ? <CheckCircle2 className="w-4 h-4" data-id="element-548" /> : item.type === 'warning' ? <AlertTriangle className="w-4 h-4" data-id="element-549" /> : <Activity className="w-4 h-4" data-id="element-550" />}
                </div>
                <div className="min-w-0 flex-1" data-id="element-551">
                  <p className="text-sm text-gray-700 leading-snug" data-id="element-552">
                    {item.message}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5 font-mono" data-id="element-553">
                    {item.time}
                  </p>
                </div>
              </div>)}
          </div>
        </motion.div>

        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-md hover:shadow-lg transition-shadow duration-200 p-6" data-id="element-554">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2" data-id="element-555">
            <Server className="w-4 h-4 text-gray-400" data-id="element-556" />
            Systeemgezondheid
          </h3>
          <div className="space-y-4" data-id="element-557">
            <div className="flex items-center justify-between" data-id="element-558">
              <span className="text-sm text-gray-600" data-id="element-559">Uptime</span>
              <span className="text-sm font-semibold text-gray-400" data-id="element-560">
                N/A (Client-side app)
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-561">
              <span className="text-sm text-gray-600" data-id="element-562">Strict Mode</span>
              <span className={`text-xs font-bold px-2 py-1 rounded-full ${strictModeOn ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-563">
                {strictModeOn ? 'AAN' : 'UIT'}
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-564">
              <span className="text-sm text-gray-600" data-id="element-565">Gem. responstijd</span>
              <span className="text-sm font-semibold text-gray-800" data-id="element-566">
                {systemHealth.responseTime}ms
              </span>
            </div>
            <div data-id="element-567">
              <div className="flex items-center justify-between mb-1.5" data-id="element-568">
                <span className="text-sm text-gray-600" data-id="element-569">Opslag</span>
                <span className="text-sm font-semibold text-gray-800" data-id="element-570">
                  {systemHealth.storageMb} / 5 MB
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-571">
                <div className="h-full bg-violet-500 rounded-full" style={{
                width: `${systemHealth.storagePct}%`
              }} data-id="element-572" />
              </div>
            </div>
            <div data-id="element-573">
              <div className="flex items-center justify-between mb-1.5" data-id="element-574">
                <span className="text-sm text-gray-600" data-id="element-575">Actief deze week</span>
                <span className="text-sm font-semibold text-gray-800" data-id="element-576">
                  {stats.activeThisWeek} leerlingen
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-577">
                <div className="h-full bg-blue-500 rounded-full" style={{
                width: `${Math.min(100, stats.activeThisWeek / Math.max(stats.totalLearners, 1) * 100)}%`
              }} data-id="element-578" />
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Bottom Row: Subscriptions, Top Learners, Validation */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-579">
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-md hover:shadow-lg transition-shadow duration-200 p-5" data-id="element-580">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4" data-id="element-581">
            Abonnementen
          </h3>
          <div className="space-y-2.5" data-id="element-582">
            {stats.planDistribution.map(item => <div key={item.label} data-id="element-583">
                <div className="flex justify-between text-xs mb-1" data-id="element-584">
                  <span className="text-gray-600" data-id="element-585">{item.label}</span>
                  <span className="font-semibold text-gray-800" data-id="element-586">
                    {item.count}
                  </span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-587">
                  <div className={`h-full rounded-full ${item.color}`} style={{
                width: `${item.pct}%`
              }} data-id="element-588" />
                </div>
              </div>)}
          </div>
        </motion.div>

        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-md hover:shadow-lg transition-shadow duration-200 p-5" data-id="element-589">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4" data-id="element-590">
            <TrendingUp className="w-3.5 h-3.5 inline mr-1" data-id="element-591" />
            Top Leerlingen
          </h3>
          {stats.topLearners.length > 0 ? <div className="space-y-2.5" data-id="element-592">
              {stats.topLearners.map((item, i) => <div key={i} className="flex items-center gap-2.5" data-id="element-593">
                  <div className="w-6 h-6 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center flex-shrink-0" data-id="element-594">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-595">
                    <p className="text-xs font-medium text-gray-800 truncate" data-id="element-596">
                      {item.name}
                    </p>
                  </div>
                  <span className="text-xs font-bold text-violet-600" data-id="element-597">
                    {item.xp.toLocaleString('nl-NL')} XP
                  </span>
                </div>)}
            </div> : <p className="text-xs text-gray-400" data-id="element-598">Geen leerlingen gevonden</p>}
        </motion.div>

        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-md hover:shadow-lg transition-shadow duration-200 p-5" data-id="element-599">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center justify-between" data-id="element-600">
            <span data-id="element-601">
              <Zap className="w-3.5 h-3.5 inline mr-1" data-id="element-602" />
              Validatiestatus
            </span>
            <div className="flex items-center gap-2" data-id="element-603">
              {/* Sample size dropdown */}
              <div className="relative" data-id="element-604">
                <button onClick={() => setSampleDropdownOpen(!sampleDropdownOpen)} className="flex items-center gap-1 text-[10px] font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded hover:bg-gray-200 transition-colors" data-id="element-605">
                  {sampleSize === 0 ? 'Alle' : sampleSize}
                  <ChevronDown className="w-3 h-3" data-id="element-606" />
                </button>
                {sampleDropdownOpen && <>
                    <div className="fixed inset-0 z-10" onClick={() => setSampleDropdownOpen(false)} data-id="element-607" />
                    <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20 min-w-[80px]" data-id="element-608">
                      {SAMPLE_SIZE_OPTIONS.map(size => <button key={size} onClick={() => {
                    setSampleSize(size);
                    setSampleDropdownOpen(false);
                  }} className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 ${sampleSize === size ? 'text-blue-600 font-semibold' : 'text-gray-700'}`} data-id="element-609">
                          {size === 0 ? 'Alle' : size}
                        </button>)}
                    </div>
                  </>}
              </div>
              <button onClick={handleRefreshPipeline} disabled={pipelineLoading} className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-40" title="Validatie vernieuwen" data-id="element-610">
                <RefreshCw className={`w-3.5 h-3.5 ${pipelineLoading ? 'animate-spin' : ''}`} data-id="element-611" />
              </button>
            </div>
          </h3>
          {pipelineLoading ? <div className="space-y-2.5" data-id="element-612">
              {['Geslaagd', 'Waarschuwingen', 'Fouten'].map(label => <div key={label} className="flex items-center justify-between" data-id="element-613">
                  <span className="text-sm text-gray-600" data-id="element-614">{label}</span>
                  <div className="h-4 w-8 bg-gray-100 rounded animate-pulse" data-id="element-615" />
                </div>)}
            </div> : pipelineResult ? <div className="space-y-2.5" data-id="element-616">
              <div className="flex items-center justify-between" data-id="element-617">
                <span className="text-sm text-gray-600" data-id="element-618">Geslaagd</span>
                <span className="text-sm font-bold text-emerald-600" data-id="element-619">
                  {passCount}
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-620">
                <span className="text-sm text-gray-600" data-id="element-621">Waarschuwingen</span>
                <span className="text-sm font-bold text-amber-600" data-id="element-622">
                  {warnCount}
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-623">
                <span className="text-sm text-gray-600" data-id="element-624">Fouten</span>
                <span className="text-sm font-bold text-red-600" data-id="element-625">
                  {failCount}
                </span>
              </div>
              <div className="pt-1 border-t border-gray-100" data-id="element-626">
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex" data-id="element-627">
                  <div className="h-full bg-emerald-500" style={{
                width: `${totalValidated > 0 ? (passCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-628" />
                  <div className="h-full bg-amber-400" style={{
                width: `${totalValidated > 0 ? (warnCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-629" />
                  <div className="h-full bg-red-500" style={{
                width: `${totalValidated > 0 ? (failCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-630" />
                </div>
                <p className="text-xs text-gray-400 mt-1.5" data-id="element-631">
                  Steekproef: {actualSampleSize} vragen
                </p>
              </div>
            </div> : <div className="space-y-2.5" data-id="element-632">
              {['Geslaagd', 'Waarschuwingen', 'Fouten'].map(label => <div key={label} className="flex items-center justify-between" data-id="element-633">
                  <span className="text-sm text-gray-600" data-id="element-634">{label}</span>
                  <span className="text-sm font-bold text-gray-300" data-id="element-635">—</span>
                </div>)}
            </div>}
        </motion.div>
      </motion.div>
    </PageLayout>;
}